import axios from "axios";
import { utils, writeFile } from "xlsx";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = "kabch_token";
export const api = axios.create({ baseURL: API, timeout: 20000 });

api.interceptors.request.use((c) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
api.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e.response?.status === 401 && !String(e.config?.url).includes("/auth/")) {
      localStorage.removeItem(TOKEN_KEY);
      window.dispatchEvent(new Event("kabch-logout"));
    }
    return Promise.reject(e);
  }
);

const FIELD_AR = { phone: "رقم الهاتف", full_name: "الاسم", ear_tag: "رقم الكبش", price: "السعر", amount: "المبلغ",
  password: "كلمة المرور", new_password: "كلمة المرور الجديدة", username: "اسم المستخدم", reason: "السبب",
  worker_name: "اسم العامل", quantity: "الكمية", default_price: "السعر الافتراضي", code: "الرمز", name: "الاسم" };

export function errMsg(e) {
  if (!e?.response) return "تعذر الاتصال بالخادم. لم يتم تأكيد حفظ العملية، تحقق من الاتصال ثم أعد المحاولة.";
  const d = e.response.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) {
    const f = d.map((x) => FIELD_AR[x?.loc?.[x.loc.length - 1]] || x?.loc?.[x.loc.length - 1]).filter(Boolean);
    return "بيانات غير صالحة في: " + [...new Set(f)].join("، ");
  }
  return "حدث خطأ غير متوقع، يرجى المحاولة مجددًا";
}

export const money = (n) => `${Math.round(Number(n || 0)).toLocaleString("en-US")} دج`;
const LOC = "ar-DZ-u-nu-latn";
export const dt = (iso) => (iso ? new Date(iso).toLocaleString(LOC, { timeZone: "Africa/Algiers", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
export const dOnly = (iso) => (iso ? new Date(iso).toLocaleDateString(LOC, { timeZone: "Africa/Algiers" }) : "—");
export const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
export const todayStr = () => new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Algiers" });

export function exportXLSX(filename, cols, rows) {
  const data = [cols.map((c) => c.label), ...rows.map((r) => cols.map((c) => { const v = c.get ? c.get(r) : r[c.key]; return v ?? ""; }))];
  const ws = utils.aoa_to_sheet(data);
  ws["!cols"] = cols.map((c, i) => ({ wch: Math.min(40, Math.max(10, ...data.slice(0, 200).map((row) => String(row[i] ?? "").length + 2))) }));
  const wb = utils.book_new();
  wb.Workbook = { Views: [{ RTL: true }] };
  utils.book_append_sheet(wb, ws, "البيانات");
  writeFile(wb, `${filename}-${todayStr()}.xlsx`);
}

export const defMethod = (s) => (s?.payment_methods || []).find((m) => m.startsWith("نقد")) || s?.payment_methods?.[0] || "نقدًا";

export function waLink(phone, text) {
  let p = String(phone || "").replace(/\D/g, "");
  if (p.startsWith("00")) p = p.slice(2);
  else if (p.startsWith("0")) p = "213" + p.slice(1);
  return `https://wa.me/${p}?text=${encodeURIComponent(text)}`;
}

export function saleWaText(s, org) {
  return [org || "الربيع للأضاحي", `وصل البيع: ${s.number}`, `التاريخ: ${dt(s.created_at)}`, `الزبون: ${s.customer_name}`, `رقم الكبش: ${s.ear_tag}`,
    `الإجمالي: ${money(s.total)}`, `المدفوع: ${money(s.paid_total)}`, `المتبقي: ${money(s.remaining)}`,
    "تنبيه: هذا الوصل لا يُعد تصريحًا لخروج الكبش."].join("\n");
}

export function paymentWaText(p, org) {
  return [org || "الربيع للأضاحي", `وصل الدفع: ${p.number}`, `التاريخ: ${dt(p.created_at)}`, `الزبون: ${p.customer_name}`, `عملية البيع: ${p.sale_number}`, `رقم الكبش: ${p.ear_tag}`,
    `المبلغ الإجمالي: ${money(p.sale_total)}`, `المبلغ المدفوع في هذا الوصل: ${money(p.amount)}`, `مجموع المدفوعات: ${money(p.new_total)}`, `المتبقي: ${money(p.remaining)}`].join("\n");
}

export function exportCSV(filename, cols, rows) {
  const esc = (v) => {
    let s = String(v ?? "");
    if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
  };
  const lines = [cols.map((c) => esc(c.label)).join(","), ...rows.map((r) => cols.map((c) => esc(c.get ? c.get(r) : r[c.key])).join(","))];
  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}-${todayStr()}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export const PAY_LABEL = { unpaid: "غير مدفوع", partial: "دفع جزئي", paid: "مدفوع بالكامل" };
export const DELIV_LABEL = { none: "لم يصدر تصريح", ready: "جاهز لإصدار التصريح", permit_issued: "صدر تصريح خروج",
  delivered: "سُلّم داخل الحظيرة", exited: "خرج فعليًا", permit_cancelled: "التصريح ملغى" };
export const PERMIT_LABEL = { valid: "صالح", used: "مستخدم", cancelled: "ملغى" };
export const ROLE_LABEL = { admin: "المدير", clerk: "موظف مكتب البيع", barn_worker: "عامل الحظيرة", gate_guard: "حارس البوابة", pen_worker: "عامل الحضيرة (تسجيل الاختيار)" };
export const SEL_LABEL = { pending: "بانتظار إتمام البيع", sold: "تم إتمام البيع", failed: "تعذر إتمام البيع", cancelled: "ملغى" };
export const SOURCE_LABEL = { barn_app: "مسجلة من الحضيرة", office_manual: "مدخلة يدويًا من المكتب" };

export function compressImage(file, max = 1280, q = 0.75) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const r = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * r); c.height = Math.round(img.height * r);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(img.src);
      resolve(c.toDataURL("image/jpeg", q));
    };
    img.onerror = () => reject(new Error("تعذر قراءة الصورة"));
    img.src = URL.createObjectURL(file);
  });
}

export async function uploadDataUrl(selectionId, dataUrl) {
  const blob = await (await fetch(dataUrl)).blob();
  const fd = new FormData();
  fd.append("file", blob, "photo.jpg");
  return api.post(`/selections/${selectionId}/photos`, fd, { timeout: 60000 });
}
